// src/components/map/hooks/adapters/useRigFracDetailHover.ts
//
// Adapter hook that manages the Rig/Frac Crew hover tooltip on the Mapbox map
// and publishes which feature is hovered to the map store. The icon-size
// adapter (useRigFracIconSize) owns applying the enlarge expression, so hover
// enlargement composes with the user size multiplier instead of overwriting it.
// Unlike useWellDetailHover, there is no dedicated Rig/FracCrew data model or
// detail-fetch API — the tiles are the only source of truth, so this hook
// renders a generic pass-through of whatever properties a hovered feature
// carries (currently `id`/`type`, auto-adapting if map-rs adds more later).
import { useEffect, useMemo, useRef, useState } from "react";

import { FRAC_LAYER, RIG_LAYER } from "constants/mapLayers.constants";
import _debounce from "lodash/debounce";
import type { PointLike } from "mapbox-gl";
import { useMapStore } from "stores/map";
import { selectWellCardPreviewOpen, useWellCardPanelStore } from "stores/wellCardPanel";
import { MapInteraction, MouseInteraction } from "types/maps/interactions";

import useBetaFeatures from "hooks/useBetaFeatures";

import { useMapContext } from "../useMapContext";

// ── Constants ────────────────────────────────────────────────────────────────

const MARGIN_FROM_VIEWPORT = 30;
const TOOLTIP_OFFSET_X = 10;
const TOOLTIP_OFFSET_Y = 50;
const DEBOUNCE_DELAY_MS = 200;
// Matches the Well/Facility tooltip gate in useWellDetailHover — keeps
// hover behavior consistent across map entities.
const MIN_ZOOM_FOR_TOOLTIP = 6;

// ── Types ────────────────────────────────────────────────────────────────────

interface RigFracDetailState {
  // `null` means "nothing hovered" — kept distinct from a real (potentially
  // empty-string) feature id so an empty tile `id` can never be mistaken for
  // the unset state.
  readonly id: Maybe<string>;
  readonly show: boolean;
  readonly x: number;
  readonly y: number;
  readonly properties: Readonly<Record<string, unknown>>;
}

export interface RigFracDetailViewModel {
  readonly show: boolean;
  readonly x: number;
  readonly y: number;
  readonly properties: Readonly<Record<string, unknown>>;
}

export interface UseRigFracDetailHoverResult {
  readonly rigFracDetail: RigFracDetailViewModel;
  readonly popupRef: React.RefObject<HTMLDivElement>;
}

// ── Initial state ────────────────────────────────────────────────────────────

const INITIAL_RIG_FRAC_DETAIL: RigFracDetailState = {
  id: null,
  show: false,
  x: 0,
  y: 0,
  properties: {}
};

const HIDDEN_DETAIL_ARGS = {
  id: null,
  show: false,
  position: { x: 0, y: 0 },
  properties: {}
} as const;

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Adapter hook that manages the Rig/Frac Crew hover tooltip on the Mapbox map
 * and publishes the hovered feature id to the map store.
 *
 * Responsibilities:
 * - Registers Mapbox mouse-move, mouse-leave, and map-movement event listeners
 *   on the Rig/Frac layers.
 * - Queries rendered features under the cursor via `queryRenderedFeatures`.
 * - Debounces tooltip display (no API fetch — properties come straight off
 *   the tile feature).
 * - Adjusts tooltip position to stay within the map viewport.
 * - Publishes the hovered feature id to the store; the icon-size adapter reads
 *   it and enlarges the icon, so hover sizing composes with the user multiplier.
 *
 * The consuming component only needs to render the tooltip UI based on the
 * returned `rigFracDetail` state and attach `popupRef` to the tooltip element.
 */
export function useRigFracDetailHover(): UseRigFracDetailHoverResult {
  const { mapbox } = useMapContext();
  const { hasFeature } = useBetaFeatures();
  const isRigFracEnabled = hasFeature("Rig And Frac Layers");

  // ── Local state ──────────────────────────────────────────────────────

  const popupRef = useRef<HTMLDivElement>(null);
  const [rigFracDetail, setRigFracDetail] = useState<RigFracDetailState>(
    INITIAL_RIG_FRAC_DETAIL
  );

  // Keep a ref to the latest rigFracDetail so event handlers always see
  // the current value without needing it in the effect dependency array
  // (which would cause listener re-registration on every state change).
  const rigFracDetailRef = useRef(rigFracDetail);
  useEffect(() => {
    rigFracDetailRef.current = rigFracDetail;
  }, [rigFracDetail]);

  // ── Core update function ─────────────────────────────────────────────
  //
  // Updates the tooltip's React state and publishes the hovered feature id to
  // the map store. The icon-size adapter (useRigFracIconSize) reads that id and
  // applies the enlarge expression, so hover sizing composes with the user's
  // size multiplier rather than overwriting it.

  const updateRigFracDetailRef = useRef(
    (args: {
      id: Maybe<string>;
      show: boolean;
      position: { x: number; y: number };
      properties: Record<string, unknown>;
    }) => {
      setRigFracDetail({
        id: args.id,
        show: args.show,
        x: args.show ? args.position.x + TOOLTIP_OFFSET_X : 0,
        y: args.show ? args.position.y - TOOLTIP_OFFSET_Y : 0,
        properties: args.properties
      });

      // Publish which feature is hovered (null when hidden). Store actions are
      // stable, so reading off getState keeps this updater out of the effect deps.
      useMapStore.getState().setHoveredRigFracId(args.show ? args.id : null);
    }
  );

  // Stable reference — the function reads from refs, not render-scoped
  // values, so it never needs to be recreated.
  const updateRigFracDetail = updateRigFracDetailRef.current;

  // The well card preview popup is a pinned reading of one well, and nothing
  // else on the map writes a second one beside it. Only the tooltip stands
  // down: the hover itself runs on, so the icon still enlarges under the
  // cursor and the hovered id the size adapter reads stays current.
  const previewOpen = useWellCardPanelStore(selectWellCardPreviewOpen);

  // ── Debounced updater (for showing tooltip) ──────────────────────────

  const updateRigFracDetailDebounce = useRef(
    _debounce(
      (args: {
        id: string;
        show: boolean;
        position: { x: number; y: number };
        properties: Record<string, unknown>;
      }) => {
        updateRigFracDetail(args);
      },
      DEBOUNCE_DELAY_MS
    )
  ).current;

  // ── Overflow adjustment ──────────────────────────────────────────────

  useEffect(() => {
    if (popupRef.current == null || !rigFracDetail.show) {
      return;
    }

    const element = popupRef.current;
    const popupRect = element.getBoundingClientRect();
    const mapElement = document.getElementById("map");
    if (!mapElement) return;
    const mapRect = mapElement.getBoundingClientRect();

    if (popupRect.right <= mapRect.right && popupRect.bottom <= mapRect.bottom) {
      return;
    }

    if (popupRect.right > mapRect.right) {
      element.style.left = `${
        rigFracDetail.x - (popupRect.right - mapRect.right + MARGIN_FROM_VIEWPORT)
      }px`;
    }

    if (popupRect.bottom > mapRect.bottom) {
      element.style.top = `${
        rigFracDetail.y - (popupRect.bottom - mapRect.bottom + MARGIN_FROM_VIEWPORT)
      }px`;
    }
  }, [rigFracDetail]);

  // ── Mapbox event listeners ───────────────────────────────────────────

  useEffect(() => {
    if (!mapbox || !isRigFracEnabled) return;

    const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
      if (mapbox.getZoom() < MIN_ZOOM_FOR_TOOLTIP) {
        if (rigFracDetailRef.current.show) {
          updateRigFracDetail(HIDDEN_DETAIL_ARGS);
          updateRigFracDetailDebounce.cancel();
        }
        return;
      }

      const buffer = 2;
      const bbox = [
        [e.point.x - buffer, e.point.y - buffer],
        [e.point.x + buffer, e.point.y + buffer]
      ] as [PointLike, PointLike];

      const layerIds = [RIG_LAYER, FRAC_LAYER].filter((id) => mapbox.getLayer(id));
      if (!layerIds.length) return;

      const features = mapbox.queryRenderedFeatures(bbox, { layers: layerIds });

      if (!features.length) {
        // Cancel unconditionally, even if nothing is showing yet — otherwise a
        // quick hover-then-move-off leaves the show-debounce pending, and it
        // can pop the tooltip up later at a stale position once the mouse stops moving
        updateRigFracDetailDebounce.cancel();
        if (rigFracDetailRef.current.show) {
          updateRigFracDetail(HIDDEN_DETAIL_ARGS);
        }
        return;
      }

      const properties = features[0].properties ?? {};
      const id = String(properties.id ?? "");

      if (rigFracDetailRef.current.id === id) return;

      updateRigFracDetailDebounce({
        id,
        show: true,
        position: e.point,
        properties
      });
    };

    const handleHide = () => {
      if (rigFracDetailRef.current.show) {
        updateRigFracDetail(HIDDEN_DETAIL_ARGS);
        updateRigFracDetailDebounce.cancel();
      }
    };

    // Unlike handleHide, this always cancels the pending debounce — even
    // when the tooltip isn't showing yet. Otherwise a hover that starts a
    // pan/zoom/rotate/pitch before the debounce fires leaves it pending, and
    // it can pop up later at a stale cursor position with nothing left to
    // hide it if the mouse has since gone idle (EVA-67).
    const handleMoveStart = () => {
      updateRigFracDetail(HIDDEN_DETAIL_ARGS);
      updateRigFracDetailDebounce.cancel();
    };

    const layerIds = [RIG_LAYER, FRAC_LAYER].filter((id) => mapbox.getLayer(id));

    mapbox.on(MouseInteraction.MouseMove, handleMouseMove);
    // Leaving the canvas straight off an icon fires no further mousemove, and the
    // layer mouseleaves exist only for layers that were loaded at registration.
    mapbox.on(MouseInteraction.MouseOut, handleMoveStart);
    layerIds.forEach((layerId) => {
      mapbox.on(MouseInteraction.MouseLeave, layerId, handleHide);
    });
    mapbox.on(MapInteraction.MoveStart, handleMoveStart);
    mapbox.on(MapInteraction.Move, handleHide);
    mapbox.on(MapInteraction.ZoomStart, handleMoveStart);
    mapbox.on(MapInteraction.RotateStart, handleMoveStart);
    mapbox.on(MapInteraction.PitchStart, handleMoveStart);

    return () => {
      mapbox.off(MouseInteraction.MouseMove, handleMouseMove);
      mapbox.off(MouseInteraction.MouseOut, handleMoveStart);
      layerIds.forEach((layerId) => {
        if (mapbox.getLayer(layerId)) {
          mapbox.off(MouseInteraction.MouseLeave, layerId, handleHide);
        }
      });
      mapbox.off(MapInteraction.MoveStart, handleMoveStart);
      mapbox.off(MapInteraction.Move, handleHide);
      mapbox.off(MapInteraction.ZoomStart, handleMoveStart);
      mapbox.off(MapInteraction.RotateStart, handleMoveStart);
      mapbox.off(MapInteraction.PitchStart, handleMoveStart);
      updateRigFracDetailDebounce.cancel();
    };
  }, [mapbox, isRigFracEnabled, updateRigFracDetail, updateRigFracDetailDebounce]);

  // ── Derived view model ─────────────────────────────────────────────

  const viewModel = useMemo<RigFracDetailViewModel>(
    () => ({
      show: mapbox != null && rigFracDetail.show && !previewOpen,
      x: rigFracDetail.x,
      y: rigFracDetail.y,
      properties: rigFracDetail.properties
    }),
    [
      mapbox,
      previewOpen,
      rigFracDetail.show,
      rigFracDetail.x,
      rigFracDetail.y,
      rigFracDetail.properties
    ]
  );

  return { rigFracDetail: viewModel, popupRef };
}
